// Texts & Email tab on the client file (Tanya 2026-10-21). Pick a template or
// write a message, choose Portal / Email / Text, send. History shows every
// email/text with its delivery status; Refresh pulls statuses and client
// replies from GoHighLevel (on demand, no background polling).
import { Mail, MessageSquare, RefreshCw, Send, Smartphone } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { request } from "../lib/api";

type Channel = "portal" | "email" | "sms";
interface Template { key: string; name: string; subject: string | null; body: string; customized?: boolean }
interface Item {
  id: string; channel: "email" | "sms"; direction: "out" | "in"; subject: string | null; body: string;
  status: string; provider: string; error: string | null; template_key: string | null; sent_by: string; created_at: string;
}
interface Payload {
  ok: true; items: Item[]; can_send: boolean; ghl_connected: boolean; has_email: boolean; has_phone: boolean;
  templates: Template[]; sync: { ok: boolean; error?: string; replies?: number } | null;
}

const STATUS_LABEL: Record<string, string> = {
  pending: "Sending…", sent: "Sent", delivered: "Delivered", read: "Opened", failed: "Failed", undelivered: "Not delivered", received: "Reply",
};
const AUTO_KEYS = new Set(["import_mfsn_reactivate", "import_mfsn_code", "import_bad_password"]);

function when(iso: string) {
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}

export function ClientCommunications({ clientId, clientName }: { clientId: string; clientName: string }) {
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [sendErr, setSendErr] = useState<string | null>(null);
  const [templateKey, setTemplateKey] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [channels, setChannels] = useState<Record<Channel, boolean>>({ portal: true, email: true, sms: false });
  const [sending, setSending] = useState(false);
  // One key per composed message: a double-click or retry never sends twice.
  const [idemKey, setIdemKey] = useState(() => crypto.randomUUID());
  const gen = useRef(0);

  async function load(sync: boolean) {
    const g = ++gen.current;
    if (sync) setSyncing(true);
    try {
      const r = await request<Payload>(`/api/clients/${clientId}/communications${sync ? "?sync=1" : ""}`);
      if (g !== gen.current) return;
      setData(r);
      setErr(r.sync && !r.sync.ok ? r.sync.error || "Could not refresh from GoHighLevel" : null);
    } catch (e: any) {
      if (g === gen.current) setErr(e?.message || "Could not load messages");
    } finally {
      if (g === gen.current) { setLoading(false); setSyncing(false); }
    }
  }
  useEffect(() => { load(true); /* eslint-disable-next-line */ }, [clientId]);
  // A changed message is a new message: new key. An unchanged retry keeps it.
  useEffect(() => { setIdemKey(crypto.randomUUID()); }, [body, subject, templateKey, channels]);

  function pickTemplate(key: string) {
    setTemplateKey(key);
    const t = data?.templates.find((x) => x.key === key);
    if (t) { setBody(t.body); setSubject(t.subject || ""); }
  }

  const available: Record<Channel, boolean> = {
    portal: true, email: !!data?.has_email, sms: !!data?.has_phone && !!data?.ghl_connected,
  };
  const chosen = (Object.keys(channels) as Channel[]).filter((c) => channels[c] && available[c]);
  const smsTooLong = chosen.includes("sms") && body.length > 1600;

  async function send() {
    if (!body.trim() || chosen.length === 0) return;
    const labels = chosen.map((c) => (c === "sms" ? "text" : c)).join(", ");
    if (!window.confirm(`Send this to ${clientName} by ${labels}?`)) return;
    setSending(true); setSendErr(null); setNote(null);
    try {
      const r = await request<{ ok: boolean; results: { channel: Channel; ok: boolean; error?: string }[] }>(
        `/api/clients/${clientId}/communications`,
        { method: "POST", body: { channels: chosen, template_key: templateKey || undefined, subject: subject || undefined, body, confirm: true, idempotency_key: idemKey } },
      );
      const failed = r.results.filter((x) => !x.ok);
      setNote(failed.length
        ? `Sent by ${r.results.filter((x) => x.ok).map((x) => x.channel).join(", ") || "nothing"}. Failed: ${failed.map((f) => `${f.channel} — ${f.error}`).join("; ")}`
        : "Sent.");
      if (!failed.length) { setBody(""); setSubject(""); setTemplateKey(""); setIdemKey(crypto.randomUUID()); }
      await load(false);
    } catch (e: any) {
      setSendErr(e?.message || "Could not send");
      await load(false);
    } finally { setSending(false); }
  }

  if (loading) return <div className="panel"><p className="muted">Loading…</p></div>;
  if (!data) return <div className="panel"><p className="error-text">{err || "Could not load messages"}</p></div>;

  const manualTemplates = data.templates.filter((t) => !AUTO_KEYS.has(t.key));
  const autoTemplates = data.templates.filter((t) => AUTO_KEYS.has(t.key));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {data.can_send && (
        <div className="panel">
          <div className="panel-header"><h2><Send size={18} aria-hidden="true" /> Send to {clientName}</h2></div>
          {!data.ghl_connected && (
            <p className="muted small" style={{ marginTop: 0 }}>
              Texts need GoHighLevel connected in Settings → Texting &amp; email provider. Email still sends from your company address.
            </p>
          )}
          <div className="stacked-form">
            <label className="form-field"><span>Template</span>
              <select value={templateKey} onChange={(e) => pickTemplate(e.target.value)}>
                <option value="">Write my own…</option>
                <optgroup label="Templates">
                  {manualTemplates.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
                </optgroup>
                <optgroup label="Report-pull alerts">
                  {autoTemplates.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
                </optgroup>
              </select>
            </label>
            {channels.email && (
              <label className="form-field"><span>Email subject</span>
                <input value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} placeholder="A message from your credit team" />
              </label>
            )}
            <label className="form-field"><span>Message</span>
              <textarea rows={6} value={body} maxLength={4000} onChange={(e) => setBody(e.target.value)}
                placeholder="Hi {{first_name}}, …" />
              <em className="muted small">
                Merge fields: {"{{first_name}} {{full_name}} {{company_name}} {{company_phone}} {{portal_link}} {{enroll_link}}"} · {body.length} characters
                {smsTooLong && <span style={{ color: "var(--ne-red)" }}> · texts are limited to 1,600 characters</span>}
              </em>
            </label>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
              <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                <input type="checkbox" checked={channels.portal} onChange={(e) => setChannels((c) => ({ ...c, portal: e.target.checked }))} />
                <MessageSquare size={14} aria-hidden="true" /> Portal
              </label>
              <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }} title={data.has_email ? "" : "No email on file"}>
                <input type="checkbox" disabled={!data.has_email} checked={channels.email && data.has_email} onChange={(e) => setChannels((c) => ({ ...c, email: e.target.checked }))} />
                <Mail size={14} aria-hidden="true" /> Email
              </label>
              <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }} title={!data.has_phone ? "No phone on file" : !data.ghl_connected ? "Connect GoHighLevel first" : ""}>
                <input type="checkbox" disabled={!data.has_phone || !data.ghl_connected} checked={channels.sms && data.has_phone && data.ghl_connected} onChange={(e) => setChannels((c) => ({ ...c, sms: e.target.checked }))} />
                <Smartphone size={14} aria-hidden="true" /> Text
              </label>
              <button className="primary-button" style={{ marginLeft: "auto" }} onClick={send}
                disabled={sending || !body.trim() || chosen.length === 0 || smsTooLong}>
                <Send size={14} aria-hidden="true" /> {sending ? "Sending…" : "Send"}
              </button>
            </div>
            {note && <p className="muted small" style={{ margin: 0 }}>{note}</p>}
            {sendErr && <p className="error-text small" style={{ margin: 0 }}>{sendErr}</p>}
          </div>
        </div>
      )}

      <div className="panel">
        <div className="panel-header">
          <h2>Texts &amp; email history</h2>
          <button className="ghost-button" style={{ marginLeft: "auto" }} onClick={() => load(true)} disabled={syncing}
            title="Refresh delivery status and replies from GoHighLevel">
            <RefreshCw size={14} aria-hidden="true" /> {syncing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        {err && <p className="error-text">{err}</p>}
        {data.items.length === 0 ? (
          <p className="muted">No texts or emails yet. Portal messages are on the Messages tab.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {data.items.map((m) => (
              <div key={m.id} className="panel" style={{ padding: 12, margin: 0, background: m.direction === "in" ? "var(--ne-surface-2, transparent)" : undefined }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  {m.channel === "sms" ? <Smartphone size={14} aria-hidden="true" /> : <Mail size={14} aria-hidden="true" />}
                  <strong>{m.direction === "in" ? `${clientName} replied` : m.channel === "sms" ? "Text" : "Email"}</strong>
                  <span className={`status-pill ${m.status}`}>{STATUS_LABEL[m.status] || m.status}</span>
                  {m.template_key && <span className="muted small">· {data.templates.find((t) => t.key === m.template_key)?.name || m.template_key}</span>}
                  {m.sent_by.startsWith("system:") && <span className="muted small">· automatic</span>}
                  <span className="muted small" style={{ marginLeft: "auto" }}>{when(m.created_at)}</span>
                </div>
                {m.subject && <div className="small" style={{ marginTop: 4 }}><strong>{m.subject}</strong></div>}
                <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{m.body}</div>
                {m.error && <p className="error-text small" style={{ margin: "4px 0 0" }}>{m.error}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
